/**
 * The state and submit lifecycle behind a "start a new conversation" composer.
 *
 * Extracted from `ConversationsHome` so the builder route can share it rather
 * than fork it. Both routes render the same `HeroChatInput` around this hook
 * and differ only in the chrome they wrap it in — the hero screen shows a logo,
 * heading, suggestions and recent chats; the builder shows the object panel.
 *
 * Owns four things a caller would otherwise re-implement:
 *
 * 1. **Composer state** — message text, uploaded files, and the entity ids
 *    posted as `entity_ids` at creation. Those ids ground the agent's FIRST
 *    chain only: `_collect_grounding_entity_ids` reads them off the
 *    conversation's `context_data`, gated on `chain_number ==
 *    grounding_chain_number` (default 1), renders record summaries and
 *    prepends that block to the first user message. Follow-up turns get no
 *    record context this way — `.../messages/` accepts only `message` and
 *    `file_ids`.
 * 2. **Seeding from route state** — a CTA can arrive with `initialPrompt` (a
 *    string of `<Entity>` mention tokens, deserialized into inline badges by
 *    the editor) and `entityIds`.
 * 3. **Clearing that route state** so Back/Forward does not re-apply a prompt
 *    the user already sent — while PRESERVING `location.search`. On the
 *    pre-conversation builder route that search string carries the entity type
 *    (and, when editing, the record), which nothing else holds yet: navigating
 *    to the bare pathname would drop them and the conversation would be created
 *    without a subject.
 * 4. **Submit** — delegating to `useStartNewConversation`, which creates the
 *    conversation and navigates to it (carrying builder mode across the hop).
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { UploadedFile } from '@/components/files';
import { useStartNewConversation } from '@/hooks/useStartNewConversation';

interface ComposerNavState {
  initialPrompt?: string;
  entityIds?: string[];
}

export interface UseNewConversationComposerResult {
  message: string;
  setMessage: (message: string) => void;
  uploadedFiles: UploadedFile[];
  setUploadedFiles: (files: UploadedFile[]) => void;
  submitting: boolean;
  error: string | null;
  /** Submit arbitrary text — used by suggestion chips. */
  submitMessage: (text: string) => Promise<void>;
  /** Submit the current message — wire to the composer's form `onSubmit`. */
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

export function useNewConversationComposer(): UseNewConversationComposerResult {
  const navigate = useNavigate();
  const location = useLocation();
  const { startNewConversation } = useStartNewConversation();

  const [message, setMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [entityIds, setEntityIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const navState = location.state as ComposerNavState | null;
    if (navState?.initialPrompt) {
      setMessage(navState.initialPrompt);
      if (navState.entityIds && navState.entityIds.length > 0) {
        setEntityIds(navState.entityIds);
      }
      // Clear nav state so back-forward does not re-apply the prompt. Keep
      // `location.search` — see note 3 in the module docstring.
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: same pattern as ReasoningChat; re-running on location changes would re-apply cleared state
  }, []);

  const submitMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) {
        return;
      }

      setSubmitting(true);
      setError(null);

      try {
        const fileIds = uploadedFiles.map(f => f.id);
        await startNewConversation(text, setError, fileIds, entityIds);
        setUploadedFiles([]);
        setEntityIds([]);
        setMessage('');
      } catch (err) {
        console.error('Failed to create conversation:', err);
        setError("We couldn't start that conversation.");
      } finally {
        setSubmitting(false);
      }
    },
    [uploadedFiles, entityIds, startNewConversation]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await submitMessage(message);
    },
    [message, submitMessage]
  );

  return {
    message,
    setMessage,
    uploadedFiles,
    setUploadedFiles,
    submitting,
    error,
    submitMessage,
    handleSubmit,
  };
}
